import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { buildQrText, QR_OPTIONS, type QrPayload } from './orderQr'

interface OrderQRCodeProps {
  qrPayload?: QrPayload | null
  qrToken?: string | null
  orderId?: string | null
  displayId?: string | number | null
  orderNumber?: string | null
  /** Rendered pixel size of the QR image (square). */
  size?: number
  /** Show the order number caption beneath the QR. */
  showLabel?: boolean
  label?: string
  style?: React.CSSProperties
}

const OrderQRCode = ({
  qrPayload,
  qrToken,
  orderId,
  displayId,
  orderNumber,
  size = 96,
  showLabel = false,
  label,
  style,
}: OrderQRCodeProps) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null)

  const text = buildQrText({ qrPayload, qrToken, orderId, displayId, orderNumber })

  useEffect(() => {
    // When there is nothing to encode the render guards on `text` and shows the
    // placeholder, so no state reset is needed here.
    if (!text) return
    let cancelled = false
    QRCode.toDataURL(text, { ...QR_OPTIONS, width: size })
      .then((url) => {
        if (!cancelled) setDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [text, size])

  if (!text) {
    return (
      <div
        title="No QR available"
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed var(--color-border, #d1d5db)',
          borderRadius: 6,
          color: 'var(--color-text-muted, #9ca3af)',
          fontSize: Math.max(8, Math.round(size / 9)),
          textAlign: 'center',
          ...style,
        }}
      >
        No QR
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        ...style,
      }}
    >
      {dataUrl ? (
        <img
          src={dataUrl}
          alt={`Order QR${orderNumber ? ` for ${orderNumber}` : ''}`}
          width={size}
          height={size}
          style={{ display: 'block', borderRadius: 4 }}
        />
      ) : (
        <div style={{ width: size, height: size }} />
      )}
      {showLabel ? (
        <span
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            color: 'var(--color-text-muted, #6b7280)',
          }}
        >
          {label ?? orderNumber}
        </span>
      ) : null}
    </div>
  )
}

export default OrderQRCode
